import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, User, ShoppingBag, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDashboard = () => {
  const { user, isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login', { state: { from: { pathname: '/dashboard' } } });
      return;
    }
    fetchOrders();
  }, [isAuthenticated, navigate, user]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders/customer/${user.email}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processing': return 'bg-yellow-100 text-yellow-800';
      case 'shipped': return 'bg-blue-100 text-blue-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (status) => {
    return status === 'paid' ? 'text-green-600' : 'text-yellow-600';
  };

  if (!isAuthenticated()) {
    return null;
  }

  return (
    <div className="section-spacing" data-testid="customer-dashboard">
      <div className="container-custom">
        <div className="mb-12">
          <h1 className="text-4xl font-heading font-bold mb-2" data-testid="dashboard-heading">
            Welcome back, {user?.name}!
          </h1>
          <p className="text-lg text-muted-foreground">Manage your orders and account</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-2xl p-6 shadow-card">
            <div className="flex items-center space-x-4">
              <div className="bg-primary/10 p-3 rounded-lg">
                <ShoppingBag className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{orders.length}</p>
                <p className="text-sm text-muted-foreground">Total Orders</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-card">
            <div className="flex items-center space-x-4">
              <div className="bg-primary/10 p-3 rounded-lg">
                <Package className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {orders.filter(o => o.order_status === 'processing').length}
                </p>
                <p className="text-sm text-muted-foreground">Processing</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-card">
            <div className="flex items-center space-x-4">
              <div className="bg-primary/10 p-3 rounded-lg">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium">{user?.email}</p>
                <p className="text-xs text-muted-foreground">Account Email</p>
              </div>
            </div>
          </div>
        </div>

        {/* Order History */}
        <div className="bg-white rounded-2xl p-8 shadow-card">
          <h2 className="text-2xl font-heading font-bold mb-6">Order History</h2>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading your orders...</p>
            </div>
          ) : orders.length > 0 ? (
            <div className="space-y-4">
              {orders.map((order) => (
                <div
                  key={order.id}
                  className="border border-border rounded-xl p-6 hover:shadow-md transition-shadow"
                  data-testid={`order-${order.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    {/* Order Info */}
                    <div className="flex-grow">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="font-mono text-sm font-semibold">
                          #{order.id.slice(0, 8)}
                        </span>
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.order_status)}`}>
                          {order.order_status}
                        </span>
                        <span className={`text-xs font-medium ${getPaymentStatusColor(order.payment_status)}`}>
                          {order.payment_status === 'paid' ? '✓ Paid' : 'Payment Pending'}
                        </span>
                      </div>

                      <div className="text-sm text-muted-foreground mb-3">
                        <p>Ordered on {new Date(order.created_at).toLocaleDateString('en-IN', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })}</p>
                      </div>

                      {/* Items */}
                      <div className="space-y-2">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex items-center space-x-3">
                            <img 
                              src={item.image} 
                              alt={item.product_name}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                            <div className="flex-grow">
                              <p className="font-medium text-sm">{item.product_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {item.variant_size} × {item.quantity}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold">₹{(item.price * item.quantity).toFixed(2)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Total & Actions */}
                    <div className="flex flex-col items-end space-y-3 md:border-l md:pl-6 md:border-border">
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Total Amount</p>
                        <p className="text-2xl font-bold text-primary">₹{order.total_amount.toFixed(2)}</p>
                      </div>
                      <button className="flex items-center space-x-2 text-sm text-primary hover:text-primary/80 transition-colors">
                        <span>View Details</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No orders yet</h3>
              <p className="text-muted-foreground mb-6">
                Start shopping and your orders will appear here
              </p>
              <a href="/products">
                <button className="btn-primary">
                  Browse Products
                </button>
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
